import React, { createContext, useContext, useState, useEffect } from 'react';
import logger from '../lib/logger';

const AuthContext = createContext(null);

// Use relative URL in production, env variable in development/preview
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Helper to parse a Response safely.
 * Uses `clone()` to be resilient to middlewares (service workers, telemetry, etc.)
 * that may have already consumed the body stream.
 */
async function parseResponse(response) {
  let raw = response;
  try {
    raw = response.clone();
  } catch {
    // Some environments (older fetch polyfills) don't support clone — fall through.
  }
  let text = '';
  try {
    text = await raw.text();
  } catch (_e) {
    // Body already read by another consumer — try the original response as last resort.
    try {
      text = await response.text();
    } catch {
      return {};
    }
  }
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(text || 'Erreur de serveur');
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Token kept in memory ONLY (not localStorage) — XSS protection.
  // The actual auth is now performed via HttpOnly cookie set by the backend.
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check current session on mount via HttpOnly cookie (no localStorage)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include',
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          // Token is in HttpOnly cookie — we don't have direct access, but we mark
          // the session as active so consumers can rely on isAuthenticated.
          setToken('cookie');
        } else {
          setUser(null);
          setToken(null);
        }
      } catch (error) {
        logger.error('Auth check failed:', error);
        setUser(null);
        setToken(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    const data = await parseResponse(response);
    if (!response.ok) {
      throw new Error(data.detail || 'Email ou mot de passe incorrect');
    }
    // Cookie was set by the server. Token in body is kept for header-based callers
    // (backwards compat) but we keep it in memory only.
    setToken(data.access_token || 'cookie');
    setUser(data.user);
    return data;
  };

  const register = async (email, password, name) => {
    const response = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, name }),
    });
    const data = await parseResponse(response);
    if (!response.ok) {
      throw new Error(data.detail || "Erreur d'inscription");
    }
    setToken(data.access_token || 'cookie');
    setUser(data.user);
    return data;
  };

  const forgotPassword = async (email) => {
    const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email }),
    });
    const data = await parseResponse(response);
    if (!response.ok) {
      throw new Error(data.detail || data.message || 'Erreur lors de la réinitialisation');
    }
    return data;
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (e) {
      // Network or server failure: log it but still clear local state to fail safe.
      logger.warn('Logout request failed:', e?.message || e);
    }
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
    login,
    register,
    forgotPassword,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
